/**
 * Recursive helpers for walking an agent object tree to either redact
 * secret fields or report which paths currently hold the redacted
 * sentinel.
 *
 * Used by the export modal (to strip secrets before download) and the
 * import wizard (to surface "secrets needed" inputs for each redacted
 * path).
 *
 * The matching rule covers `apiKey`, any key ending in `ApiKey`, plus
 * `subscriptionKey` and `subscriptionToken` (the latter two are used by
 * the Bing and Brave web-search provider configs respectively — see
 * `frontend/src/types/webSearch.types.ts`). New secret-bearing fields
 * added to the agent shape MUST be added here.
 *
 * This must stay in lock-step with the backend mirror in
 * `backend/src/services/agentExportImportService.ts`.
 */
import { REDACTED_SENTINEL } from './schema';

function isSecretKey(key: string): boolean {
  return (
    key === 'apiKey' ||
    key.endsWith('ApiKey') ||
    key === 'subscriptionKey' ||
    key === 'subscriptionToken'
  );
}

export function redactSecrets<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => redactSecrets(item)) as unknown as T;
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      out[key] = isSecretKey(key) ? REDACTED_SENTINEL : redactSecrets(val);
    }
    return out as unknown as T;
  }
  return value;
}

export function findRedactedPaths(
  value: unknown,
  prefix = ''
): string[] {
  const paths: string[] = [];
  if (Array.isArray(value)) {
    value.forEach((item, idx) => {
      paths.push(...findRedactedPaths(item, `${prefix}[${idx}]`));
    });
    return paths;
  }
  if (value !== null && typeof value === 'object') {
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      const path = prefix ? `${prefix}.${key}` : key;
      if (isSecretKey(key) && val === REDACTED_SENTINEL) {
        paths.push(path);
      } else {
        paths.push(...findRedactedPaths(val, path));
      }
    }
  }
  return paths;
}
